<template>
  <div>
    <div class="d-flex align-items-center mb-2">
      <b-button-group size="sm">
        <b-button
          :variant="mode === 'visual' ? 'primary' : 'outline-primary'"
          @click="switchMode('visual')"
        >
          Візуальний
        </b-button>
        <b-button
          :variant="mode === 'json' ? 'primary' : 'outline-primary'"
          @click="switchMode('json')"
        >
          JSON
        </b-button>
      </b-button-group>
    </div>

    <!-- Visual mode -->
    <div v-if="mode === 'visual'">
      <b-alert variant="info" :show="!fields.length" class="py-1 px-1">
        Полів ще немає. Натисніть "Додати поле" щоб почати.
      </b-alert>

      <draggable
        v-model="fields"
        handle=".drag-handle"
        @end="onDragEnd"
        tag="div"
      >
        <b-list-group-item
          v-for="(field, index) in fields"
          :key="index"
          class="d-flex flex-column py-2 mb-1"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              icon="MenuIcon"
              size="16"
              class="drag-handle mr-1"
              style="cursor: grab"
            />
            <strong class="mr-1">{{ field.label }}</strong>
            <b-badge variant="light-primary" class="mr-1">
              {{ getTypeName(field.type) }}
            </b-badge>
            <b-badge
              v-if="field.required"
              variant="light-danger"
              class="mr-auto"
            >
              обов'язк.
            </b-badge>
            <span v-else class="mr-auto"></span>
            <b-button
              size="sm"
              variant="flat-primary"
              class="btn-icon p-25"
              @click="editField(index)"
            >
              <feather-icon icon="EditIcon" size="14" />
            </b-button>
            <b-button
              size="sm"
              variant="flat-secondary"
              class="btn-icon p-25"
              @click="duplicateField(index)"
            >
              <feather-icon icon="CopyIcon" size="14" />
            </b-button>
            <b-button
              size="sm"
              variant="flat-danger"
              class="btn-icon p-25"
              @click="removeField(index)"
            >
              <feather-icon icon="TrashIcon" size="14" />
            </b-button>
          </div>
          <small v-if="field.show_when" class="text-muted mt-25">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            └─ показувати коли: <strong>{{ field.show_when.field }}</strong> =
            "{{ field.show_when.value }}"
          </small>
        </b-list-group-item>
      </draggable>

      <b-button
        variant="outline-success"
        size="sm"
        class="mt-1"
        @click="addField"
      >
        + Додати поле
      </b-button>
    </div>

    <!-- JSON mode -->
    <div v-else>
      <b-alert variant="danger" :show="!!jsonError" class="py-1 px-1">
        {{ jsonError }}
      </b-alert>
      <b-form-textarea
        v-model="jsonText"
        rows="15"
        class="text-monospace"
        style="font-size: 12px"
        @input="onJsonInput"
      />
    </div>

    <!-- Field modal -->
    <b-modal
      v-model="showFieldModal"
      :title="fieldUsecase === 'add' ? 'Додати поле' : 'Редагувати поле'"
      hide-footer
      centered
      no-close-on-backdrop
      size="md"
    >
      <b-form-group label="Назва (label)">
        <b-form-input
          v-model="currentField.label"
          placeholder="Наприклад: ПІБ"
        />
      </b-form-group>

      <b-form-group label="Ключ (key)">
        <b-form-input
          v-model="currentField.key"
          placeholder="Наприклад: full_name"
        />
        <small class="text-muted">
          Унікальний ідентифікатор поля (латиниця, без пробілів)
        </small>
      </b-form-group>

      <b-form-group label="Тип поля">
        <b-form-select
          v-model="currentField.type"
          :options="fieldTypes"
        />
      </b-form-group>

      <b-form-group label="Placeholder">
        <b-form-input
          v-model="currentField.placeholder"
          placeholder="Текст-підказка"
        />
      </b-form-group>

      <b-form-checkbox v-model="currentField.required" class="mb-2">
        Обов'язкове поле
      </b-form-checkbox>

      <!-- Options for select -->
      <div v-if="currentField.type === 'select'" class="mb-2">
        <b-form-group label="Тип опцій">
          <b-form-select
            v-model="currentField.option_type"
            :options="optionTypes"
            size="sm"
          />
        </b-form-group>

        <label class="d-block font-weight-bold">Опції</label>
        <draggable v-model="currentField.options" handle=".option-drag-handle" tag="div">
          <div
            v-for="(opt, oi) in currentField.options"
            :key="oi"
            class="d-flex align-items-center mb-50"
          >
            <feather-icon
              icon="MenuIcon"
              size="14"
              class="option-drag-handle mr-50"
              style="cursor: grab; flex-shrink: 0"
            />
            <!-- Text option -->
            <b-form-input
              v-if="currentField.option_type === 'text'"
              :value="opt"
              @input="updateOption(oi, $event)"
              size="sm"
            />
            <!-- Date option -->
            <date-picker
              v-else-if="currentField.option_type === 'date'"
              :value="parseOptionDate(opt)"
              @input="updateOptionDate(oi, $event)"
              type="date"
              format="DD.MM.YYYY"
              value-type="format"
              placeholder="Оберіть дату"
              class="w-100"
            />
            <!-- Datetime option -->
            <date-picker
              v-else-if="currentField.option_type === 'datetime'"
              :value="parseOptionDate(opt)"
              @input="updateOptionDate(oi, $event)"
              type="datetime"
              format="DD.MM.YYYY HH:mm"
              value-type="format"
              :time-picker-options="{ start: '08:00', step: '00:15', end: '20:00' }"
              placeholder="Оберіть дату і час"
              class="w-100"
            />
            <b-button
              size="sm"
              variant="flat-danger"
              class="btn-icon p-25 ml-50"
              @click="removeOption(oi)"
            >
              <feather-icon icon="XIcon" size="14" />
            </b-button>
          </div>
        </draggable>
        <!-- Add new option -->
        <div class="d-flex align-items-center">
          <b-form-input
            v-if="currentField.option_type === 'text'"
            v-model="newOption"
            size="sm"
            placeholder="Нова опція"
            @keyup.enter="addOption"
          />
          <date-picker
            v-else-if="currentField.option_type === 'date'"
            v-model="newOptionDate"
            type="date"
            format="DD.MM.YYYY"
            value-type="format"
            placeholder="Оберіть дату"
            class="w-100"
          />
          <date-picker
            v-else-if="currentField.option_type === 'datetime'"
            v-model="newOptionDate"
            type="datetime"
            format="DD.MM.YYYY HH:mm"
            value-type="format"
            :time-picker-options="{ start: '08:00', step: '00:15', end: '20:00' }"
            placeholder="Оберіть дату і час"
            class="w-100"
          />
          <b-button
            size="sm"
            variant="outline-primary"
            class="ml-50"
            @click="addOption"
            :disabled="!canAddOption"
          >
            +
          </b-button>
        </div>
      </div>

      <!-- Show when condition -->
      <div class="mb-2">
        <label class="d-block font-weight-bold">
          Умова показу (необов'язково)
        </label>
        <div v-if="currentField.show_when">
          <div class="d-flex align-items-center mb-50">
            <small class="mr-50">Показувати коли поле:</small>
            <b-form-select
              v-model="currentField.show_when.field"
              :options="availableFieldsForCondition"
              size="sm"
              style="max-width: 200px"
            />
          </div>
          <div class="d-flex align-items-center mb-50">
            <small class="mr-50">має значення:</small>
            <b-form-select
              v-if="conditionValueOptions.length > 0"
              v-model="currentField.show_when.value"
              :options="conditionValueOptions"
              size="sm"
              style="max-width: 200px"
            />
            <b-form-input
              v-else
              v-model="currentField.show_when.value"
              size="sm"
              placeholder="Значення"
              style="max-width: 200px"
            />
          </div>
          <b-button
            size="sm"
            variant="outline-danger"
            @click="currentField.show_when = null"
          >
            Прибрати умову
          </b-button>
        </div>
        <b-button
          v-else
          size="sm"
          variant="outline-secondary"
          @click="currentField.show_when = { field: '', value: '' }"
        >
          + Додати умову
        </b-button>
      </div>

      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-1" @click="cancelField">
          Скасувати
        </b-button>
        <b-button
          variant="primary"
          :disabled="!currentField.key || !currentField.label"
          @click="saveField"
        >
          {{ fieldUsecase === 'add' ? 'Додати' : 'Зберегти' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/uk";
export default {
  components: { draggable, DatePicker },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      fields: [],
      mode: "visual",
      jsonText: "",
      jsonError: "",
      showFieldModal: false,
      fieldUsecase: "add",
      editIndex: null,
      newOption: "",
      newOptionDate: null,
      currentField: this.emptyField(),
      optionTypes: [
        { text: "Текст", value: "text" },
        { text: "Дата", value: "date" },
        { text: "Дата і час", value: "datetime" },
      ],
      fieldTypes: [
        { text: "Текст", value: "text" },
        { text: "Текстове поле", value: "textarea" },
        { text: "Вибір зі списку", value: "select" },
        { text: "Телефон", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Чекбокс", value: "checkbox" },
        { text: "Дата", value: "date" },
        { text: "Дата і час", value: "datetime" },
        { text: "Файл", value: "file" },
      ],
    };
  },
  computed: {
    availableFieldsForCondition() {
      return this.fields
        .filter((f, i) => i !== this.editIndex)
        .map((f) => ({ text: f.label + " (" + f.key + ")", value: f.key }));
    },
    canAddOption() {
      if (this.currentField.option_type === "text") {
        return this.newOption.trim().length > 0;
      }
      return !!this.newOptionDate;
    },
    conditionValueOptions() {
      if (!this.currentField.show_when || !this.currentField.show_when.field) {
        return [];
      }
      const targetField = this.fields.find(
        (f) => f.key === this.currentField.show_when.field
      );
      if (targetField && targetField.type === "select" && targetField.options) {
        return targetField.options.map((o) => ({ text: o, value: o }));
      }
      return [];
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(val) {
        if (Array.isArray(val)) {
          this.fields = JSON.parse(JSON.stringify(val));
        }
      },
    },
  },
  methods: {
    emptyField() {
      return {
        key: "",
        label: "",
        type: "text",
        required: false,
        placeholder: "",
        options: [],
        option_type: "text",
        show_when: null,
      };
    },
    getTypeName(type) {
      const found = this.fieldTypes.find((t) => t.value === type);
      return found ? found.text : type;
    },
    emitUpdate() {
      this.$emit("input", JSON.parse(JSON.stringify(this.fields)));
    },
    addField() {
      this.fieldUsecase = "add";
      this.editIndex = null;
      this.currentField = this.emptyField();
      this.newOption = "";
      this.newOptionDate = null;
      this.showFieldModal = true;
    },
    editField(index) {
      this.fieldUsecase = "edit";
      this.editIndex = index;
      const field = JSON.parse(JSON.stringify(this.fields[index]));
      if (!field.option_type) field.option_type = "text";
      this.currentField = field;
      this.newOption = "";
      this.newOptionDate = null;
      this.showFieldModal = true;
    },
    saveField() {
      // Auto-add pending option before saving
      if (this.currentField.type === "select") {
        if (this.currentField.option_type === "text" && this.newOption.trim()) {
          this.currentField.options.push(this.newOption.trim());
          this.newOption = "";
        } else if (this.newOptionDate) {
          this.currentField.options.push(this.newOptionDate);
          this.newOptionDate = null;
        }
      }
      const field = JSON.parse(JSON.stringify(this.currentField));
      if (field.show_when && (!field.show_when.field || !field.show_when.value)) {
        field.show_when = null;
      }
      if (this.fieldUsecase === "add") {
        this.fields.push(field);
      } else {
        this.$set(this.fields, this.editIndex, field);
      }
      this.showFieldModal = false;
      this.emitUpdate();
    },
    cancelField() {
      this.showFieldModal = false;
    },
    removeField(index) {
      if (confirm("Видалити поле?")) {
        this.fields.splice(index, 1);
        this.emitUpdate();
      }
    },
    duplicateField(index) {
      const copy = JSON.parse(JSON.stringify(this.fields[index]));
      copy.key = copy.key + "_copy";
      copy.label = copy.label + " (копія)";
      this.fields.splice(index + 1, 0, copy);
      this.emitUpdate();
    },
    addOption() {
      if (this.currentField.option_type === "text") {
        if (this.newOption.trim()) {
          this.currentField.options.push(this.newOption.trim());
          this.newOption = "";
        }
      } else {
        if (this.newOptionDate) {
          this.currentField.options.push(this.newOptionDate);
          this.newOptionDate = null;
        }
      }
    },
    updateOption(index, value) {
      this.$set(this.currentField.options, index, value);
    },
    updateOptionDate(index, value) {
      this.$set(this.currentField.options, index, value || "");
    },
    parseOptionDate(opt) {
      return opt || null;
    },
    removeOption(index) {
      this.currentField.options.splice(index, 1);
    },
    onDragEnd() {
      this.emitUpdate();
    },
    switchMode(newMode) {
      if (newMode === this.mode) return;
      if (newMode === "json") {
        this.jsonText = JSON.stringify(this.fields, null, 2);
        this.jsonError = "";
      } else {
        try {
          const parsed = JSON.parse(this.jsonText);
          if (!Array.isArray(parsed)) {
            this.jsonError = "JSON має бути масивом";
            return;
          }
          this.fields = parsed;
          this.emitUpdate();
          this.jsonError = "";
        } catch (e) {
          this.jsonError = "Невалідний JSON: " + e.message;
          return;
        }
      }
      this.mode = newMode;
    },
    onJsonInput() {
      try {
        const parsed = JSON.parse(this.jsonText);
        if (Array.isArray(parsed)) {
          this.fields = parsed;
          this.emitUpdate();
          this.jsonError = "";
        } else {
          this.jsonError = "JSON має бути масивом";
        }
      } catch (e) {
        this.jsonError = "Невалідний JSON: " + e.message;
      }
    },
  },
};
</script>

<style scoped>
.drag-handle:hover,
.option-drag-handle:hover {
  color: #7367f0;
}
</style>
